import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateTools, TOption } from '@treezor/design-system';

import { ISearchUser, UserStatusLabel } from '@/features/user/store/user.model';
import {
  ICheckField,
  IPeriod,
  IPeriodField,
  ISearch,
  ISearchField,
  ISelectField,
  periodBases,
} from '@/ui-kit/table-filters/filters.type';
import { isBetween, isFrom, isTo } from '@/ui-kit/table-filters/PeriodFilter';

import { useHasFilter } from '@/hooks/useHasFilter';
import { ISearchDate, IUsersFiltersForm } from '@/features/user/store/allUsers.types';

export function mapUsersCreated(value) {
  if (isFrom(value) && value.base === periodBases.ON) {
    const toDate = new Date(value.from).getTime();
    return {
      createdDateFrom: value.from,
      createdDateTo: DateTools.format(new Date(toDate + 86399900), 'api-date-time'),
    };
  } else if (isFrom(value) && value.base === periodBases.AFTER) {
    return {
      createdDateFrom: value.from,
    };
  } else if (isTo(value)) {
    return {
      createdDateTo: DateTools.format(new Date(value.to).getTime() + 86399900, 'api-date-time'),
    };
  } else if (isBetween(value)) {
    return {
      createdDateFrom: value.from,
      createdDateTo: DateTools.format(new Date(value.to).getTime() + 86399900, 'api-date-time'),
    };
  }
}

export const parseAllUsersFiltersValue = (
  key: keyof IUsersFiltersForm,
  value: IUsersFiltersForm[keyof IUsersFiltersForm],
): ISearchUser => {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (key === 'createdDate') {
    return mapUsersCreated(value);
  } else if (key === 'search') {
    return { [(value as ISearch).key]: (value as ISearch).value };
  } else {
    return { [key]: value === null ? undefined : value };
  }
};

export interface IUseUsersFiltersProps {
  onChange: (queryParameters: ISearchUser) => void;
  initialValues: IUsersFiltersForm;
}

export function useAllUsersFilters({ onChange, initialValues = {} }: IUseUsersFiltersProps) {
  const { t } = useTranslation();

  const { getValues, reset, setValue, watch } = useForm<IUsersFiltersForm>({
    defaultValues: {
      search: null,
      userStatus: null,
      createdDate: {
        from: null,
        to: null,
        base: periodBases.ON,
      },
      ...initialValues,
    },
  });

  const filtersDeps = JSON.stringify(initialValues);

  useEffect(() => {
    const filters = JSON.parse(filtersDeps);
    Object.keys(filters).map((key: keyof IUsersFiltersForm) => {
      setValue(key, filters[key]);
    });
  }, [filtersDeps, setValue]);

  const searchOptions: TOption[] = useMemo(
    () => [
      { label: t('pages.dashboard.users.filters.userId'), value: 'userId' },
      { label: t('pages.dashboard.users.filters.name'), value: 'name' },
      { label: t('pages.dashboard.users.filters.legalName'), value: 'legalName' },
      { label: t('pages.dashboard.users.filters.email'), value: 'email' },
      { label: t('pages.dashboard.users.filters.tag'), value: 'userTag' },
    ],
    [t],
  );

  const userStatusOptions: TOption[] = useMemo(
    () =>
      Object.keys(UserStatusLabel)
        .filter((status) => status !== UserStatusLabel.BLOCKED) // awaiting API
        .map((status: string) => ({
          label: t(`pages.dashboard.users.status.${status}`),
          value: UserStatusLabel[status],
        })),
    [t],
  );

  const specifiedUSPerson: TOption[] = useMemo(
    () => [
      {
        label: t('pages.dashboard.users.filters.flag.usTaxResident'),
        value: 1,
      },
    ],
    [t],
  );

  const searchValue = watch('search');
  const userStatusValue = watch('userStatus');
  const usTaxResidentValue = watch('specifiedUSPerson');
  const createdDateValue = watch('createdDate');

  const getQueryParameters = useCallback(() => {
    const values = getValues();
    return Object.keys(values).reduce<ISearchUser>((prev, key: keyof IUsersFiltersForm) => {
      return {
        ...prev,
        ...parseAllUsersFiltersValue(key, values[key]),
      };
    }, {});
  }, [getValues]);

  const handleChange = useCallback(() => {
    onChange(getQueryParameters());
  }, [getQueryParameters, onChange]);

  const handleClear = useCallback(() => {
    reset({
      search: null,
    });
    onChange(null);
  }, [reset, onChange]);

  const handleValueChange = useCallback(
    (key: keyof IUsersFiltersForm, value: ISearch | ISearchDate | number) => {
      setValue(key, value);
      handleChange();
    },
    [setValue, handleChange],
  );

  const handleSearchChange = useCallback(
    (value: ISearch) => {
      const currentValue = getValues('search');
      const newValue = Object.keys(value).reduce<ISearch>((prev, current) => {
        return {
          ...prev,
          [current]: value[current] === null ? currentValue[current] : value[current],
        };
      }, {} as ISearch);
      setValue('search', newValue);
      if (value.key !== null && value.value !== null) {
        handleChange();
      }
    },
    [getValues, handleChange, setValue],
  );

  const handleCreatedDateChange = useCallback(
    (value: IPeriod) => {
      if (value.base === null && value.from === null && value.to === null) {
        reset();
        handleChange();
        return;
      }

      const currentValue = getValues('createdDate');
      const newValue = Object.keys(value).reduce<IPeriod>((prev, current) => {
        return {
          ...prev,
          [current]: value[current] === null ? currentValue[current] : value[current],
        };
      }, {} as IPeriod);
      setValue('createdDate', newValue);

      if (isFrom(newValue) || isTo(newValue) || isBetween(newValue)) {
        handleChange();
      }
    },
    [getValues, handleChange, setValue, reset],
  );

  const searchField: ISearchField = useMemo(
    () => ({
      value: searchValue,
      handleChange: handleSearchChange,
      options: searchOptions,
    }),
    [handleSearchChange, searchOptions, searchValue],
  );

  const userStatusField: ISelectField = useMemo(
    () => ({
      value: userStatusOptions.find((option) => option.value === userStatusValue),
      handleChange: (value: number) => handleValueChange('userStatus', value),
      options: userStatusOptions,
    }),
    [handleValueChange, userStatusOptions, userStatusValue],
  );

  const specifiedUSPersonField: ICheckField = useMemo(
    () => ({
      values: !!usTaxResidentValue ? specifiedUSPerson : [],
      handleChange: (values: number[]) => handleValueChange('specifiedUSPerson', values?.[0]),
      options: specifiedUSPerson,
    }),
    [handleValueChange, specifiedUSPerson, usTaxResidentValue],
  );

  const createdDateField: IPeriodField = useMemo(
    () => ({ value: createdDateValue, handleChange: handleCreatedDateChange }),
    [createdDateValue, handleCreatedDateChange],
  );

  const hasFilters = useHasFilter({
    searchField,
    userStatusField,
    createdDateField,
    usTaxResidentField: specifiedUSPersonField,
  });

  return {
    searchField,
    userStatusField,
    createdDateField,
    usTaxResidentField: specifiedUSPersonField,
    clearFilters: handleClear,
    hasFilters,
  };
}
